/**
 * Recurring Invoice Templates — list, stat cards, and per-row actions.
 * The create dialog lives in `components/recurring/RecurringInvoiceCreateModal`.
 *
 * Backend scheduler also auto-generates drafts whenever a template's
 * `next_run_at` falls due (5-minute resolution); this UI is mainly for
 * set-up + ad-hoc nudges. Generated invoices land in the same store as
 * user-created ones, so the Invoices page shows them automatically with a
 * `Generated from recurring` provenance flag.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Play, Pause, Trash2, RefreshCw, Repeat, Calendar, DollarSign,
} from 'lucide-react';
import { fetchWithAuth } from '../services/TokenRefresh';
import syncService from '../services/SyncService';
import RecurringInvoiceCreateModal from '../components/recurring/RecurringInvoiceCreateModal';
import { ActionTile } from '../components/ui/ActionTile';
import { TINT } from '../components/ui/IconTile';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_PILL = {
  active: { bg: '#dcfce7', fg: '#15803d', label: 'Active' },
  paused: { bg: '#fef3c7', fg: '#a16207', label: 'Paused' },
  ended:  { bg: '#e5e7eb', fg: '#475569', label: 'Ended' },
};

// How many of *that* cadence's invoices land per month, used by the
// "Estimated MRR" stat. 4.33 ≈ avg weeks/month, 2.17 ≈ biweekly.
const CADENCE_TO_MONTHLY = {
  weekly: 4.33,
  biweekly: 2.17,
  monthly: 1,
  quarterly: 1 / 3,
  yearly: 1 / 12,
};

function authHeaders() {
  // Only content-type — auth header comes from fetchWithAuth. Kept as a
  // helper for readability at call sites.
  return { 'Content-Type': 'application/json' };
}

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

function itemsTotal(t) {
  const subtotal = (t.items || []).reduce(
    (s, it) => s + Number(it.amount || it.quantity * it.rate || 0),
    0,
  );
  const taxed = Math.max(0, subtotal - Number(t.discount || 0));
  const tax = taxed * Number(t.tax_rate || 0) / 100;
  return taxed + tax - Number(t.deposit_amount || 0);
}

function EmptyState({ onCreate }) {
  return (
    <div data-testid="recurring-empty" style={{
      background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 16,
      padding: '52px 24px', textAlign: 'center', color: '#475569',
    }}>
      <Repeat size={32} style={{ color: '#0073ea', marginBottom: 8 }} />
      <h3 style={{ margin: 0, color: '#0f172a', fontSize: 18, fontWeight: 700 }}>
        No recurring schedules yet
      </h3>
      <p style={{ margin: '8px auto 18px', maxWidth: 380, fontSize: 14 }}>
        Set up a template once and we&apos;ll generate a draft invoice on your chosen
        cadence — weekly, bi-weekly, monthly, quarterly, or yearly. You stay in
        control: every generated invoice starts as a draft for your review.
      </p>
      <button
        data-testid="recurring-empty-create"
        onClick={onCreate}
        style={{
          background: '#0073ea', color: '#fff', border: 'none', padding: '10px 18px',
          borderRadius: 10, fontWeight: 600, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 8,
        }}
      >
        <Plus size={16} /> Create first schedule
      </button>
    </div>
  );
}

export default function RecurringInvoices() {
  const workspaceId = syncService.getSyncKey() || syncService.userId;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [prefill, setPrefill] = useState(null);

  // Read + consume the "Make Recurring" prefill dropped in sessionStorage by
  // the Invoices tab. Auto-opens the create modal with the invoice's
  // customer + line items so the user only picks the cadence + schedule.
  useEffect(() => {
    const consume = () => {
      try {
        const raw = sessionStorage.getItem('maidly_recurring_prefill');
        if (!raw) return;
        sessionStorage.removeItem('maidly_recurring_prefill');
        const data = JSON.parse(raw);
        setPrefill(data);
        setCreateOpen(true);
        toast.info(`Set the cadence to turn ${data.source_invoice_number || 'this invoice'} into a recurring schedule`);
      } catch (_) { /* ignore */ }
    };
    consume();
    window.addEventListener('maidly:recurring-prefill', consume);
    return () => window.removeEventListener('maidly:recurring-prefill', consume);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/api/recurring-invoices?workspace_id=${encodeURIComponent(workspaceId)}`,
        { headers: authHeaders(), credentials: 'include' },
      );
      const data = await res.json();
      if (data?.success) setTemplates(data.templates || []);
    } catch (e) {
      toast.error('Failed to load recurring schedules');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchAll();
    // Customers come from localStorage so the form can show a dropdown.
    try {
      const c = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
      setCustomers(Array.isArray(c) ? c : []);
    } catch { setCustomers([]); }
  }, [fetchAll]);

  // Cross-tab sync: refresh the list when another view dispatches a
  // `maidly:recurring-updated` event (e.g. Invoices → "Make Recurring") or
  // when the user brings the window back to focus.
  useEffect(() => {
    const refresh = () => {
      fetchAll();
      try {
        const c = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
        setCustomers(Array.isArray(c) ? c : []);
      } catch { /* ignore */ }
    };
    window.addEventListener('maidly:recurring-updated', refresh);
    window.addEventListener('maidly:customers-updated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('maidly:recurring-updated', refresh);
      window.removeEventListener('maidly:customers-updated', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [fetchAll]);

  const generateNow = async (id) => {
    try {
      const res = await fetchWithAuth(
        `${API_URL}/api/recurring-invoices/${id}/generate-now`,
        { method: 'POST', headers: authHeaders(), credentials: 'include' },
      );
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.detail || 'Failed to generate');
      }
      toast.success(`Draft ${data.invoice?.invoiceNumber || ''} created`);
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'Failed to generate invoice');
    }
  };

  const togglePause = async (t) => {
    const nextStatus = t.status === 'paused' ? 'active' : 'paused';
    try {
      const res = await fetchWithAuth(`${API_URL}/api/recurring-invoices/${t.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error('Update failed');
      toast.success(nextStatus === 'paused' ? 'Paused' : 'Resumed');
      fetchAll();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete recurring schedule for ${t.customer_name}?`)) return;
    try {
      const res = await fetchWithAuth(`${API_URL}/api/recurring-invoices/${t.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Schedule deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const stats = useMemo(() => {
    const active = templates.filter((t) => t.status === 'active').length;
    const paused = templates.filter((t) => t.status === 'paused').length;
    const mrr = templates
      .filter((t) => t.status === 'active')
      .reduce((sum, t) => sum + itemsTotal(t) * (CADENCE_TO_MONTHLY[t.cadence] || 0), 0);
    return { active, paused, mrr };
  }, [templates]);

  return (
    <div data-testid="recurring-page" style={{ padding: 20, maxWidth: 1280, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 18, gap: 12, flexWrap: 'wrap',
      }}>
        <div>
          <h1 style={{
            margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Repeat size={26} color="#0073ea" /> Recurring invoices
          </h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            Set a cadence once — we generate a draft invoice for review on every cycle.
          </p>
        </div>
        <button
          data-testid="recurring-new-btn"
          onClick={() => setCreateOpen(true)}
          style={{
            background: '#0073ea', color: '#fff', border: 'none', padding: '10px 18px',
            borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        ><Plus size={16} /> New schedule</button>
      </div>

      {/* Stat cards */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 14, marginBottom: 22,
      }}>
        {[
          { key: 'active', label: 'Active', value: stats.active, icon: Play, tint: '#16a34a' },
          { key: 'paused', label: 'Paused', value: stats.paused, icon: Pause, tint: '#f59e0b' },
          { key: 'mrr', label: 'Estimated MRR', value: fmtMoney(stats.mrr), icon: DollarSign, tint: '#0073ea' },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key}
              data-testid={`recurring-stat-${c.key}`}
              style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14,
                padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8,
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: `${c.tint}1f`, color: c.tint,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={16} />
              </div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: '#64748b',
                textTransform: 'uppercase', letterSpacing: 0.4,
              }}>{c.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{c.value}</div>
            </div>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div data-testid="recurring-loading" style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
          padding: 60, textAlign: 'center', color: '#64748b',
        }}>
          <RefreshCw size={20} className="spin" style={{ marginBottom: 8 }} />
          <div>Loading…</div>
        </div>
      ) : templates.length === 0 ? (
        <EmptyState onCreate={() => setCreateOpen(true)} />
      ) : (
        <div data-testid="recurring-list" style={{
          background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 1.2fr 1fr auto',
            padding: '12px 20px', background: '#f8fafc',
            fontSize: 11, fontWeight: 700, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: 0.4,
            borderBottom: '1px solid #e5e7eb',
          }}>
            <div>Customer</div>
            <div>Cadence</div>
            <div>Total / cycle</div>
            <div>Next run</div>
            <div>Status</div>
            <div>Actions</div>
          </div>
          {templates.map((t) => {
            const pill = STATUS_PILL[t.status] || STATUS_PILL.ended;
            return (
              <div
                key={t.id}
                data-testid={`recurring-row-${t.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1.2fr 1fr auto',
                  padding: '14px 20px',
                  alignItems: 'center',
                  borderBottom: '1px solid #f1f5f9',
                  fontSize: 14,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{t.customer_name || 'Unknown'}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>
                    {t.generated_count || 0} invoice{(t.generated_count || 0) === 1 ? '' : 's'} generated
                  </div>
                </div>
                <div style={{ textTransform: 'capitalize', color: '#475569' }}>
                  {t.cadence}
                </div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>
                  {fmtMoney(itemsTotal(t))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                  <Calendar size={14} /> {fmtDate(t.next_run_at)}
                </div>
                <div>
                  <span data-testid={`recurring-status-${t.id}`} style={{
                    padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                    background: pill.bg, color: pill.fg,
                  }}>{pill.label}</span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <ActionTile
                    testid={`recurring-generate-${t.id}`}
                    onClick={() => generateNow(t.id)}
                    disabled={t.status === 'ended'}
                    title="Generate draft now"
                    icon={RefreshCw}
                    tint={TINT.invoices}
                  />
                  <ActionTile
                    testid={`recurring-pause-${t.id}`}
                    onClick={() => togglePause(t)}
                    disabled={t.status === 'ended'}
                    title={t.status === 'paused' ? 'Resume' : 'Pause'}
                    icon={t.status === 'paused' ? Play : Pause}
                    tint={t.status === 'paused' ? TINT.green : '#f59e0b'}
                  />
                  <ActionTile
                    testid={`recurring-delete-${t.id}`}
                    onClick={() => remove(t)}
                    title="Delete"
                    icon={Trash2}
                    tint={TINT.expenses}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <RecurringInvoiceCreateModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setPrefill(null); }}
        onSaved={() => fetchAll()}
        customers={customers}
        workspaceId={workspaceId}
        prefill={prefill}
      />
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; display: inline-block; }
      `}</style>
    </div>
  );
}
